import type { LoadFlowScenarioInputs } from '../../../..';
import { ActivePower } from '../../../../domain/ActivePower';
import { ReactivePower } from '../../../../domain/ReactivePower';
import type {
  FunctionalGrid,
  LoadInputPoints
} from '../../../../grid/FunctionalGrid';
import type {
  TransformerEvaluatedAvailableCapacity,
  TransformerEvaluatedLoading,
  TransformerEvaluatedRatedCapacity
} from '../../../loadFlowSummary';

export function getSumActivePowerAndSumReactivePower(
  evaluatedPowerTransformer: {
    id: `{${string}}`;
    objectType: 'PowerTransformer';
    results: {
      loadingPercent: TransformerEvaluatedLoading;
      availableCapacity: TransformerEvaluatedAvailableCapacity;
      ratedCapacity: TransformerEvaluatedRatedCapacity;
    };
  },
  functionalGrid: FunctionalGrid,
  inputs: LoadFlowScenarioInputs
) {
  const powerTransformerId = evaluatedPowerTransformer.id;

  const powerTransformerEnds = functionalGrid.powerTransformers.find(
    p => p.objectId === powerTransformerId
  )?.powerTransformerEnds;

  if (powerTransformerEnds === undefined) {
    return {
      sumActivePower: ActivePower.fromWattNumber(undefined),
      sumReactivePower: ReactivePower.fromVarNumber(undefined)
    };
  }
  const isTwoWindingTransformer = powerTransformerEnds.length === 2;
  if (isTwoWindingTransformer) {
    return getSumActivePowerAndSumReactivePowerForTwoWindingTransformer(
      powerTransformerId,
      functionalGrid,
      inputs
    );
  }
  const isThreeWindingTransformer = powerTransformerEnds.length === 3;
  if (isThreeWindingTransformer) {
    return getSumActivePowerAndSumReactivePowerForThreeWindingTransformer(
      powerTransformerId,
      functionalGrid,
      inputs
    );
  }

  return {
    sumActivePower: ActivePower.fromWattNumber(undefined),
    sumReactivePower: ReactivePower.fromVarNumber(undefined)
  };
}

type AvailableActivePowerAndReactivePower = {
  status: 'AVAILABLE';
  sumActivePower: ActivePower;
  sumReactivePower: ReactivePower;
};
type NotAvailableActivePowerAndReactivePower = {
  status: 'NOT_AVAILABLE';
  activePower: null;
  reactivePower: null;
};
type ActivePowerAndReactivePower =
  | AvailableActivePowerAndReactivePower
  | NotAvailableActivePowerAndReactivePower;

function getSumActivePowerAndSumReactivePowerForTwoWindingTransformer(
  powerTransformerId: string,
  functionalGrid: FunctionalGrid,
  inputs: LoadFlowScenarioInputs
) {
  // EndNumber1 is the ending from HV site of trafo
  const end2 = functionalGrid.powerTransformerEnds.find(
    end => end.endNumber === 2
  );

  const lvBusbars = functionalGrid.lowVoltageBusbars.map(b => b.objectId);

  if (end2 === undefined) {
    return {
      sumActivePower: ActivePower.fromWattNumber(undefined),
      sumReactivePower: ReactivePower.fromVarNumber(undefined)
    };
  }

  const end2NotTrafoConnectivity =
    end2.connectivity1 === powerTransformerId
      ? end2.connectivity2
      : end2.connectivity1;

  const resultEnd2NotTrafoConnectivity = tryToActivePowerAndReactivePower(
    end2NotTrafoConnectivity,
    lvBusbars,
    functionalGrid.loadInputPoints,
    inputs
  );
  if (resultEnd2NotTrafoConnectivity.status === 'AVAILABLE') {
    return {
      sumActivePower: resultEnd2NotTrafoConnectivity.sumActivePower,
      sumReactivePower: resultEnd2NotTrafoConnectivity.sumReactivePower
    };
  }

  return {
    sumActivePower: ActivePower.fromWattNumber(undefined),
    sumReactivePower: ReactivePower.fromVarNumber(undefined)
  };
}

function getSumActivePowerAndSumReactivePowerForThreeWindingTransformer(
  powerTransformerId: string,
  functionalGrid: FunctionalGrid,
  inputs: LoadFlowScenarioInputs
) {
  // EndNumber1 is the ending from HV site of trafo
  const end2 = functionalGrid.powerTransformerEnds.find(
    end => end.endNumber === 2
  );
  const end3 = functionalGrid.powerTransformerEnds.find(
    end => end.endNumber === 3
  );
  const lvBusbars = functionalGrid.lowVoltageBusbars.map(b => b.objectId);

  if (end2 === undefined || end3 === undefined) {
    return {
      sumActivePower: ActivePower.fromWattNumber(undefined),
      sumReactivePower: ReactivePower.fromVarNumber(undefined)
    };
  }

  const end2NotTrafoConnectivity =
    end2.connectivity1 === powerTransformerId
      ? end2.connectivity2
      : end2.connectivity1;
  const end3NotTrafoConnectivity =
    end3.connectivity1 === powerTransformerId
      ? end3.connectivity2
      : end3.connectivity1;

  const resultEnd2NotTrafoConnectivity = tryToActivePowerAndReactivePower(
    end2NotTrafoConnectivity,
    lvBusbars,
    functionalGrid.loadInputPoints,
    inputs
  );
  const resultEnd3NotTrafoConnectivity = tryToActivePowerAndReactivePower(
    end3NotTrafoConnectivity,
    lvBusbars,
    functionalGrid.loadInputPoints,
    inputs
  );

  if (
    resultEnd2NotTrafoConnectivity.status === 'AVAILABLE' &&
    resultEnd3NotTrafoConnectivity.status === 'AVAILABLE'
  ) {
    const end2ActivePower = resultEnd2NotTrafoConnectivity.sumActivePower;
    const end2ReactivePower = resultEnd2NotTrafoConnectivity.sumReactivePower;

    const end3ActivePower = resultEnd3NotTrafoConnectivity.sumActivePower;
    const end3ReactivePower = resultEnd3NotTrafoConnectivity.sumReactivePower;

    if (
      end2ActivePower === undefined ||
      end2ReactivePower === undefined ||
      end3ActivePower === undefined ||
      end3ReactivePower === undefined
    ) {
      return {
        sumActivePower: ActivePower.fromWattNumber(undefined),
        sumReactivePower: ReactivePower.fromVarNumber(undefined)
      };
    }

    const activePowerSum = end2ActivePower.addTo(end3ActivePower);
    const reactivePowerSum = end2ReactivePower.addTo(end3ReactivePower);

    return {
      sumActivePower: activePowerSum,
      sumReactivePower: reactivePowerSum
    };
  }

  return {
    sumActivePower: ActivePower.fromWattNumber(undefined),
    sumReactivePower: ReactivePower.fromVarNumber(undefined)
  };
}

function tryToActivePowerAndReactivePower(
  connectivityId: `{${string}}`,
  lvBusbars: `{${string}}`[],
  loadInputPoints: LoadInputPoints,
  inputs: LoadFlowScenarioInputs
): ActivePowerAndReactivePower {
  if (lvBusbars.includes(connectivityId)) {
    const loadInputPoint = loadInputPoints.find(
      i => i.connectedToId === connectivityId
    );
    const load = inputs.loads.find(i => i.id === loadInputPoint?.id);
    if (load !== undefined) {
      return {
        status: 'AVAILABLE',
        sumActivePower: ActivePower.fromWattNumber(load.p),
        sumReactivePower: ReactivePower.fromVarNumber(load.q)
      };
    }
  }
  return {
    status: 'NOT_AVAILABLE',
    activePower: null,
    reactivePower: null
  };
}
